<template>
  <lf-modal
    :title="$t('OFFER_CUSTOMISATION.OFFER_DESCRIPTION_MODAL_TITLE')"
    :close="onClose"
  >
    <template v-slot:content>
      <form @submit.prevent="onSubmit">
        <div class="space-y-5 p-6">
          <lf-input
            :placeholder="$t('OFFER_CUSTOMISATION.PRODUCT_NAME')"
            showLabel
            name="name"
            :value="offerTemplate.name"
          ></lf-input>
          <lf-dropdown
            :options="PRODUCT_TYPE_OPTIONS"
            name="product"
            :placeholder="$t('OFFER_CUSTOMISATION.PRODUCT_CATEGORY')"
            showLabel
            full-width
            :value="offerTemplate.product"
            :disabled="offerTemplate.product"
            @change="setProductType"
          />
          <lf-textarea
            ref="description"
            name="description"
            :placeholder="$t('OFFER_CUSTOMISATION.PRODUCT_DESCRIPTION')"
            :maxlength="1000"
            showLabel
            :value="offerTemplate?.description ?? ''"
            :characterLimit="1000"
            rows="7"
          />
        </div>
        <div class="flex justify-end space-x-4 border-t p-6">
          <outline-button @click="onClose">
            {{ $t("COMMON.CANCEL") }}
          </outline-button>
          <primary-button type="submit" :disabled="disableSave">
            {{ $t("COMMON.SAVE") }}
          </primary-button>
        </div>
      </form>
    </template>
  </lf-modal>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";

import { useForm } from "vee-validate";

import { OfferProductType } from "@/enums/offers";
import { PRODUCT_TYPE_OPTIONS } from "@/helpers/constants/offers";
import { EMPTY_TEMPLATE } from "@/helpers/constants/offers";
import { usePromiseWrapper } from "@/hooks/common";
import { useActiveWorkflowTemplate } from "@/hooks/workflow";
import offerTemplateService from "@/services/modules/offerTemplate";

import LfModal from "@/components/ui/Modal.vue";

import type { IFunder } from "@/models/funders";
import type { EmptyOfferTemplate, OfferTemplate } from "@/models/offers";
import type { OfferOptions } from "@/models/options";

const props = withDefaults(
  defineProps<{
    offerTemplate?: OfferTemplate | EmptyOfferTemplate;
    currentFunder?: IFunder;
    isEdit?: boolean;
  }>(),
  {
    offerTemplate: () => EMPTY_TEMPLATE,
    isEdit: false,
  }
);

const emit = defineEmits<{
  templateCreated: [OfferTemplate];
  templateUpdated: [OfferTemplate];
  close: [];
}>();

const { getters } = useStore();

const { activeTemplate } = useActiveWorkflowTemplate();

const defaultTemplate = ref<OfferTemplate | null>(null);

const productType = ref<OfferProductType | undefined>(
  props.offerTemplate.product
);

const defaultTemplatesMap = computed(
  () =>
    (getters["options/offerOptions"] as OfferOptions).fallback_templates
      .product_mapping
);

const { handleSubmit, isSubmitting, values } = useForm({
  initialValues: {
    product: props.offerTemplate.product,
    name: props.offerTemplate.name,
    description: props.offerTemplate.description,
  },
});

const disableSave = computed(() => {
  return !defaultTemplate.value || isSubmitting.value || !values.name;
});

const { fetchWrapper: getDefaultOfferTemplate } = usePromiseWrapper(
  async (templateId: string) => {
    const offerTemplatesResponse =
      await offerTemplateService.getTemplate(templateId);
    defaultTemplate.value = offerTemplatesResponse.data;
  }
);

const setProductType = async (value: OfferProductType) => {
  productType.value = value;
  if (props.isEdit) {
    return;
  }
  const defaultTemplateId = defaultTemplatesMap.value[value];
  if (defaultTemplateId) {
    await getDefaultOfferTemplate(defaultTemplateId);
  }
};

const onClose = () => {
  emit("close");
};

const onSubmit = handleSubmit(async (values) => {
  const funderId = props.currentFunder?.id;
  const clientId = activeTemplate.value.client?.id ?? null;
  if (defaultTemplate.value === null || !funderId) {
    return;
  }

  const tempalteContent = props.isEdit
    ? props.offerTemplate.content
    : defaultTemplate.value.content;

  const newTemplate: OfferTemplate | EmptyOfferTemplate = {
    ...props.offerTemplate,
    ...values,
    product: productType.value,
    content: {
      ...tempalteContent,
      product: productType.value ?? OfferProductType.ARLOC,
    },
    funder_id: funderId,
    client_id: clientId,
  };

  if (props.isEdit) {
    const createdTemplate =
      await offerTemplateService.updateTemplate(newTemplate);
    emit("templateUpdated", createdTemplate);
  } else {
    const createdTemplate =
      await offerTemplateService.createTemplate(newTemplate);
    emit("templateCreated", createdTemplate);
  }
  emit("close");
});

onMounted(() => {
  if (!productType.value) {
    return;
  }
  getDefaultOfferTemplate(defaultTemplatesMap.value[productType.value]);
});
</script>
